import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useChangePassword,
	useDeleteMfa,
	useMfaMethodsQuery,
} from "@/hooks/use-me";
import { useAuthStore } from "@/stores/auth.store";

const passwordSchema = z
	.object({
		currentPassword: z.string().min(1, "Mot de passe actuel requis"),
		newPassword: z.string().min(12, "12 caractères minimum"),
		confirm: z.string().min(1, "Confirmation requise"),
	})
	.refine((d) => d.newPassword === d.confirm, {
		message: "Les deux mots de passe ne correspondent pas",
		path: ["confirm"],
	});

type PwdForm = z.infer<typeof passwordSchema>;

export function ProfileSecurityPage() {
	const { data: methods } = useMfaMethodsQuery();
	const changePwdMutation = useChangePassword();
	const deleteMfaMutation = useDeleteMfa();
	const navigate = useNavigate();
	const logout = useAuthStore((s) => s.logout);

	const [pwdError, setPwdError] = useState<string | null>(null);
	const [pwdSuccess, setPwdSuccess] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<PwdForm>({
		resolver: zodResolver(passwordSchema),
		defaultValues: { currentPassword: "", newPassword: "", confirm: "" },
	});

	const newPwd = watch("newPassword");
	const score = scorePassword(newPwd);

	const submitPassword = handleSubmit(async (data) => {
		setPwdError(null);
		setPwdSuccess(false);
		try {
			await changePwdMutation.mutateAsync({
				currentPassword: data.currentPassword,
				newPassword: data.newPassword,
			});
			reset();
			setPwdSuccess(true);
			// Le backend a invalidé toutes les autres sessions ; la courante reste valide jusqu'à
			// l'expiration du JWT. On informe simplement l'utilisateur.
		} catch (err) {
			setPwdError(extractErrorMessage(err, "Changement impossible"));
		}
	});

	return (
		<div className="stack">
			<Card>
				<CardHead
					title="Mot de passe"
					sub="Argon2id · politique CDC §3.1.2 · 5 derniers MdP interdits à la réutilisation"
				/>
				<CardBody>
					<form onSubmit={submitPassword} style={{ maxWidth: 520 }} noValidate>
						<FormRow label="Mot de passe actuel" required>
							<input
								type="password"
								className="aform-i"
								autoComplete="current-password"
								data-testid="profile-pwd-current"
								{...register("currentPassword")}
							/>
							{errors.currentPassword && (
								<div
									style={{
										color: "var(--a-danger)",
										fontSize: 11,
										marginTop: 4,
									}}
								>
									{errors.currentPassword.message}
								</div>
							)}
						</FormRow>
						<FormRow
							label="Nouveau mot de passe"
							required
							helper="12 car. min, maj/min/num/spécial"
						>
							<input
								type="password"
								className="aform-i"
								autoComplete="new-password"
								data-testid="profile-pwd-new"
								{...register("newPassword")}
							/>
							{newPwd && (
								<div className="pwd-meter" style={{ marginTop: 8 }}>
									<div className={score >= 1 ? "l1" : ""} />
									<div className={score >= 3 ? "l2" : ""} />
									<div className={score >= 5 ? "l3" : ""} />
								</div>
							)}
							{errors.newPassword && (
								<div
									style={{
										color: "var(--a-danger)",
										fontSize: 11,
										marginTop: 4,
									}}
								>
									{errors.newPassword.message}
								</div>
							)}
						</FormRow>
						<FormRow label="Confirmer le nouveau mot de passe" required>
							<input
								type="password"
								className="aform-i"
								autoComplete="new-password"
								data-testid="profile-pwd-confirm"
								{...register("confirm")}
							/>
							{errors.confirm && (
								<div
									style={{
										color: "var(--a-danger)",
										fontSize: 11,
										marginTop: 4,
									}}
								>
									{errors.confirm.message}
								</div>
							)}
						</FormRow>

						{pwdError && (
							<div
								className="auth-alert danger"
								data-testid="profile-pwd-error"
							>
								<span className="icon">
									<AdminIcon n="alert" s={16} />
								</span>
								<div>{pwdError}</div>
							</div>
						)}
						{pwdSuccess && (
							<div
								className="auth-alert success"
								data-testid="profile-pwd-success"
							>
								<span className="icon">
									<AdminIcon n="check" s={16} />
								</span>
								<div>
									<strong>Mot de passe modifié</strong>
									Vos autres sessions ont été déconnectées par mesure de
									sécurité.
								</div>
							</div>
						)}

						<div
							style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
						>
							<AdminButton
								variant="primary"
								type="submit"
								disabled={isSubmitting}
								data-testid="profile-pwd-submit"
							>
								{isSubmitting ? "Modification…" : "Modifier le mot de passe"}
							</AdminButton>
						</div>
					</form>
				</CardBody>
			</Card>

			<Card>
				<CardHead
					title="Authentification à deux facteurs"
					sub="MFA obligatoire pour Super Admin / Admin (CDC §3.1.1)"
					right={
						<AdminButton
							variant="primary"
							size="sm"
							onClick={() => navigate("/profil/securite/2fa/methode")}
							data-testid="profile-mfa-add"
						>
							<AdminIcon n="plus" s={12} />
							Ajouter une méthode
						</AdminButton>
					}
				/>
				<CardBody>
					{(!methods || methods.length === 0) && (
						<div className="empty">
							Aucune méthode MFA configurée. Cliquez sur « Ajouter une méthode »
							pour commencer.
						</div>
					)}
					{methods?.map((m) => (
						<div
							key={m.id}
							data-testid={`mfa-method-${m.type}`}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 12,
								padding: "12px 0",
								borderBottom: "1px solid var(--a-border)",
							}}
						>
							<div
								style={{
									width: 40,
									height: 40,
									borderRadius: 8,
									background: "var(--a-terra-soft)",
									color: "var(--a-terracotta)",
									display: "grid",
									placeItems: "center",
								}}
							>
								<AdminIcon
									n={
										m.type === "totp"
											? "qr"
											: m.type === "sms"
												? "phone"
												: "mail"
									}
									s={18}
								/>
							</div>
							<div style={{ flex: 1 }}>
								<strong style={{ fontSize: 13 }}>
									{m.type === "totp"
										? "Application authenticator"
										: m.type === "sms"
											? "SMS"
											: m.type === "email"
												? "Email"
												: "WhatsApp Business"}
								</strong>
								<div className="muted" style={{ fontSize: 11 }}>
									{m.label ?? "—"} · ajoutée le{" "}
									{new Date(m.createdAt).toLocaleDateString("fr-FR")}
								</div>
							</div>
							{m.isDefault && (
								<Tag variant="terra" noDot>
									Par défaut
								</Tag>
							)}
							{m.verified && (
								<Tag variant="success" noDot>
									<AdminIcon n="check" s={11} /> Vérifiée
								</Tag>
							)}
							<button
								className="top-btn"
								title="Supprimer"
								onClick={() => deleteMfaMutation.mutate(m.id)}
								data-testid={`mfa-delete-${m.type}`}
							>
								<AdminIcon n="close" s={14} c="var(--a-danger)" />
							</button>
						</div>
					))}
				</CardBody>
			</Card>

			<Card>
				<CardHead title="Zone sensible" />
				<CardBody>
					<AdminButton
						variant="ghost"
						style={{ color: "var(--a-danger)" }}
						onClick={() => {
							logout();
							navigate("/login");
						}}
						data-testid="profile-logout"
					>
						<AdminIcon n="alert" s={14} />
						Me déconnecter
					</AdminButton>
				</CardBody>
			</Card>
		</div>
	);
}

function scorePassword(p: string): number {
	if (!p) return 0;
	let s = 0;
	if (p.length >= 12) s++;
	if (/[A-Z]/.test(p)) s++;
	if (/[a-z]/.test(p)) s++;
	if (/\d/.test(p)) s++;
	if (/[!@#$%&*\-_+=?]/.test(p)) s++;
	return s;
}
